import { useReadContract, useReadContracts } from 'wagmi';
import type { Address } from 'viem';
import { bondDepositoryAbi, erc20Abi, referralManagerAbi, sSyntraAbi, stakingManagerAbi } from '@syntra/abi';
import { DEPLOYMENT as D } from '../config/env.ts';

export interface PositionData {
  syntra?: bigint;
  usdt?: bigint;
  bnb?: bigint;
  lp?: bigint;
  /** SYN staked across every plan (the sSYN balance). */
  staked?: bigint;
  principalUsd?: bigint;
  referralPendingUsd?: bigint;
  capRemainingUsd?: bigint;
  bound?: boolean;
  rank?: number;
  bondIds?: readonly bigint[];
}

/** The connected user's balances and positions across the protocol, in one multicall. */
export function usePosition(address: Address | undefined) {
  const enabled = Boolean(address);
  const a = address ?? '0x0000000000000000000000000000000000000000';
  const q = useReadContracts({
    allowFailure: true,
    query: { enabled, refetchInterval: 15_000 },
    contracts: [
      { address: D.syntra, abi: erc20Abi, functionName: 'balanceOf', args: [a] },
      { address: D.usdt, abi: erc20Abi, functionName: 'balanceOf', args: [a] },
      { address: D.syntraUsdtPair, abi: erc20Abi, functionName: 'balanceOf', args: [a] },
      { address: D.sSyntra, abi: sSyntraAbi, functionName: 'balanceOf', args: [a] },
      { address: D.staking, abi: stakingManagerAbi, functionName: 'lotCount', args: [a] },
      { address: D.staking, abi: stakingManagerAbi, functionName: 'noLockLot', args: [a] },
      { address: D.staking, abi: stakingManagerAbi, functionName: 'principalUsd', args: [a] },
      { address: D.referral, abi: referralManagerAbi, functionName: 'pendingUsd', args: [a] },
      { address: D.referral, abi: referralManagerAbi, functionName: 'capRemainingUsd', args: [a] },
      { address: D.referral, abi: referralManagerAbi, functionName: 'isBound', args: [a] },
      { address: D.referral, abi: referralManagerAbi, functionName: 'rankOf', args: [a] },
      { address: D.bonds, abi: bondDepositoryAbi, functionName: 'positionsOf', args: [a] },
    ],
  });
  const r = q.data;
  const v = <T,>(i: number) => (r?.[i]?.status === 'success' ? (r[i].result as T) : undefined);
  const data: PositionData = {
    syntra: v<bigint>(0),
    usdt: v<bigint>(1),
    lp: v<bigint>(2),
    staked: v<bigint>(3),
    principalUsd: v<bigint>(6),
    referralPendingUsd: v<bigint>(7),
    capRemainingUsd: v<bigint>(8),
    bound: v<boolean>(9),
    rank: v<number>(10),
    bondIds: v<readonly bigint[]>(11),
  };
  return { data, isLoading: enabled && q.isLoading, refetch: q.refetch };
}

export interface StakeLot {
  id: number;
  plan: number; // the plan it is in now (an ended lock that was withdrawn from is in plan 0)
  origin: number; // the plan it was opened in (decides its exit fee)
  unlockAt: number; // seconds; Number.MAX_SAFE_INTEGER for the permanent plan
  value: bigint; // SYN now, with every compounded reward
  withdrawable: bigint; // what can come out now (permanent: the rewards above the deposit)
  principal: bigint; // SYN deposited after the fee (the permanent plan's locked part)
  principalUsd: bigint;
  empty: boolean;
}

/** Every staking lot of the user with its live value and what is withdrawable, plus the no-lock position. */
export function useStakes(address: Address | undefined) {
  const enabled = Boolean(address);
  const list = useReadContract({
    address: D.staking,
    abi: stakingManagerAbi,
    functionName: 'lots',
    args: [address ?? '0x0000000000000000000000000000000000000000'],
    query: { enabled, refetchInterval: 15_000 },
  });
  const raw = list.data ?? [];
  const values = useReadContracts({
    allowFailure: false,
    query: { enabled: enabled && raw.length > 0, refetchInterval: 15_000 },
    contracts: raw.flatMap((_, i) => [
      { address: D.staking, abi: stakingManagerAbi, functionName: 'lotValue', args: [address!, BigInt(i)] } as const,
      { address: D.staking, abi: stakingManagerAbi, functionName: 'withdrawable', args: [address!, BigInt(i)] } as const,
    ]),
  });
  const v = values.data as bigint[] | undefined;
  const lots: StakeLot[] = raw.map((l, i) => ({
    id: i,
    plan: Number(l.plan),
    origin: Number(l.origin),
    unlockAt: l.unlockAt >= 2n ** 53n ? Number.MAX_SAFE_INTEGER : Number(l.unlockAt),
    value: v?.[i * 2] ?? 0n,
    withdrawable: v?.[i * 2 + 1] ?? 0n,
    principal: l.principal,
    principalUsd: l.principalUsd,
    empty: l.shares === 0n,
  }));
  const noLock = lots.find((l) => l.origin === 0);
  return { lots, noLock, isLoading: enabled && (list.isLoading || values.isLoading) };
}

